import _isEmpty from 'lodash/isEmpty';
import { bool, func, object, string } from 'prop-types';
import React from 'react';
import _get from 'lodash/get';

import Typography from 'ravenjs/lib/Typography';
import ButtonLink from 'ravenjs/lib/Button/ButtonLink';
import { stringReplaceWithJSX } from 'ravenjs/utils/string';

import Loading from 'components/Loading';
import LoginForm from 'components/LoginForm';
import {
    DB_FAILURE_ERROR_MESSAGE,
    ERROR_CODES,
    GENERIC_MESSAGE,
    GENERIC_MESSAGE_KEYS,
} from 'constants/login';
import { EXTERNAL_LINKS } from 'constants/app';
import MESSAGES from 'constants/messages';
import { ACCOUNT_PASSWORD_RESET } from 'constants/account';

import { sanitizeMarkup } from 'utils/sanitize';
import LoginContent from './LoginContent';
import LoginErrors from './LoginErrors';
import LoginStyled from './LoginStyled';
import SupportActions from './SupportActions';
import TermsAndConditionsStyled from './TermsAndConditionsStyled';

const LoginFormWrapper = ({
    demo,
    error,
    formData,
    handleForgotPassword,
    handleLoginSubmit,
    handleTechSupport,
    header,
    loading,
    loginLogoUrl,
}) => {
    const linksMap = {
        TERMS_OF_SERVICE: (
            <ButtonLink
                disabled={demo}
                fontSize="0.8rem"
                key="terms"
                target="_blank"
                href={EXTERNAL_LINKS.EULA.TERMS_OF_SERVICE}
            >
                {MESSAGES.LOGIN.EULA.LINK_TEXT.TERMS_OF_USE}
            </ButtonLink>
        ),
        PRIVACY_POLICY: (
            <ButtonLink
                disabled={demo}
                fontSize="0.8rem"
                key="policy"
                target="_blank"
                href={EXTERNAL_LINKS.EULA.PRIVACY_POLICY}
            >
                {MESSAGES.LOGIN.EULA.LINK_TEXT.PRIVACY_POLICY}
            </ButtonLink>
        ),
    };

    const renderErrors = () => {
        let errorDescription = GENERIC_MESSAGE;
        const errorCode = _get(error, 'errorCode', '');

        if (!_isEmpty(error)) {
            Object.values(GENERIC_MESSAGE_KEYS).forEach(value => {
                if (error.description.toLowerCase().includes(value)) {
                    errorDescription = error.description;
                }
            });
        }

        switch (errorCode) {
            case ERROR_CODES.DB_ERROR:
                errorDescription = DB_FAILURE_ERROR_MESSAGE;
                break;
            case ERROR_CODES.LOGIN_ATTEMPT:
                errorDescription = errorDescription.replace(
                    ACCOUNT_PASSWORD_RESET.RESET_YOUR_PASSWORD_1,
                    ACCOUNT_PASSWORD_RESET.RESET_YOUR_PASS_LINK_1
                );
                break;
            case ERROR_CODES.LOGIN_ATTEMPT_EXCEEDED:
                errorDescription = errorDescription.replace(
                    ACCOUNT_PASSWORD_RESET.RESET_YOUR_PASSWORD_2,
                    ACCOUNT_PASSWORD_RESET.RESET_YOUR_PASS_LINK_2
                );
                break;
            default:
                break;
        }

        let errorMap = '';
        if (
            errorCode === ERROR_CODES.LOGIN_ATTEMPT ||
            errorCode === ERROR_CODES.LOGIN_ATTEMPT_EXCEEDED
        ) {
            errorMap = errorDescription.split('.');
        }

        return (
            !_isEmpty(error) && (
                <LoginErrors>
                    {errorMap.length ? (
                        errorMap.map(error => {
                            if (error !== '') {
                                return (
                                    <Typography
                                        key={error}
                                        style={{ margin: 0 }}
                                        dangerouslySetInnerHTML={sanitizeMarkup(`${error}.`)}
                                    />
                                );
                            } else return null;
                        })
                    ) : (
                        <Typography
                            style={{ margin: 0 }}
                            dangerouslySetInnerHTML={sanitizeMarkup(errorDescription)}
                        />
                    )}
                </LoginErrors>
            )
        );
    };

    return (
        <LoginStyled>
            <Loading loading={loading} />
            {renderErrors()}
            <LoginContent>
                <LoginForm disabled={demo} formData={formData} onSubmit={handleLoginSubmit} />
                <SupportActions
                    disabled={demo}
                    onForgotPassword={handleForgotPassword}
                    onTechSupportHelp={handleTechSupport}
                />
                <TermsAndConditionsStyled fontFamily="Favorit">
                    {stringReplaceWithJSX(MESSAGES.LOGIN.EULA.DESCRIPTION, linksMap)}
                </TermsAndConditionsStyled>
            </LoginContent>
        </LoginStyled>
    );
};

LoginFormWrapper.propTypes = {
    demo: bool,
    error: object,
    formData: object,
    handleForgotPassword: func,
    handleLoginSubmit: func,
    handleTechSupport: func,
    header: string,
    loading: bool,
    loginLogoUrl: string,
};

LoginFormWrapper.defaultProps = {
    demo: false,
    error: {},
    formData: {},
    handleForgotPassword: () => {},
    handleLoginSubmit: () => {},
    handleTechSupport: () => {},
    header: '',
    loading: false,
    loginLogoUrl: '',
};

export default LoginFormWrapper;
